import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { filter, map, Observable, Subject, takeUntil } from 'rxjs';

import { ICreateKeywordsDialogData } from 'editing-tool/src/app/shared/components/keyword/create-keywords-dialog/create-keywords-dialog-data';
import { CreateKeywordsDialogComponent } from 'editing-tool/src/app/shared/components/keyword/create-keywords-dialog/create-keywords-dialog.component';
import { IKeywordListData } from 'editing-tool/src/app/shared/components/keyword/keyword-list-data';
import { Renderers, ScConfirmationService, TableSettings } from 'sc-common';
import { ApiPeriodicalKeywordListItem } from 'sc-common/core/services/open-api/main-api-client';

@Component({
    selector: 'et-keyword-list',
    templateUrl: './keyword-list.component.html',
    standalone: false
})
export class KeywordListComponent implements OnInit, OnDestroy {

    @Input()
    public data: IKeywordListData;

    @Input()
    public subHeader: string;

    @Output()
    public readonly keywordsCountChanged = new EventEmitter<void>();

    public tableSettings: TableSettings<ApiPeriodicalKeywordListItem>;

    public isReadonlyWrapper$: Observable<{ isReadonly: boolean; }>;

    private readonly _destroy$ = new Subject<void>();

    private readonly _refreshTable$ = new Subject<void>();

    constructor(
        private readonly _confirmationService: ScConfirmationService,
        private readonly _dialogService: DialogService) {
    }

    public ngOnInit(): void {
        this._initTable();
        this.isReadonlyWrapper$ = this.data.isReadonly$.pipe(map(isReadonly => ({ isReadonly })));
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    public write(): void {

        const dialogData: ICreateKeywordsDialogData = {
            createKeywords: this.data.createKeywords
        };

        const dialogRef = this._dialogService.open(CreateKeywordsDialogComponent, {
            width: '35vw',
            header: $localize`Write keywords`,
            closable: true,
            data: dialogData
        });

        dialogRef.onClose
            .pipe(
                filter(result => !!result),
                takeUntil(this._destroy$))
            .subscribe(() => this.onKeywordCountChanged());
    }

    public onKeywordCountChanged(): void {
        this._refreshTable$.next();
        this.keywordsCountChanged.emit();
    }

    public onRemoveKeyword(keywordId: number): void {
        this._confirmationService.confirm({
            title: $localize`Remove Keyword`,
            message: $localize`Are you sure to remove this keyword?`,
            acceptLabel: $localize`Yes`,
            rejectLabel: $localize`No`,
            accept$: this.data.remove(keywordId),
            acceptCallback: () => this.onKeywordCountChanged()
        });
    }

    private _initTable(): void {
        this.tableSettings = TableSettings.create(ApiPeriodicalKeywordListItem, {
            dataSource: oDataQuery => this.data.list(oDataQuery),
            columns: {
                keyword: {
                    header: $localize`Keywords`,
                    renderer: Renderers.rawHtml(),
                    sortable: false,
                    filterable: false
                }
            },
            defaultSort: { field: x => x.keyword, order: 'asc' },
            reload$: this._refreshTable$,
            columnResizeMode: 'fit',
            paginator: false,
            rowHover: false,
            hasFilters: false
        });
    }
}
